import { ELanguage } from '@microfrontends/app-shell-v2'
import React, { ReactNode, useContext, useReducer } from 'react'
import { createReverseTranslateFunction, createTFunction } from '../helpers/common'
import { TAuthUser } from '../services/userService'
import { EProfileStrengthForm, IProfileData, TProfileInfo } from '../services/profileTypes'
import reducer from './reducer'
import { JobAlertContext, JobAlertProvider } from './JobAlertStore'
import { TProfileLevelItem } from '../components/ProfileStrength/const'
import { TRecommendation } from '../components/Recommendation'
import { HogProvider } from '@microfrontends/react-components'
import ProfileSettingContainer from '../components/ProfileSettingContainer'

export enum EDevice {
    MOBILE = 'mobile',
    TABLET = 'tablet',
    LAPTOP = 'laptop',
    DESKTOP = 'desktop'
}

export type TBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

type TStoreProps = {
    children: ReactNode
    langCode: ELanguage
}

export type TResumeSettingItem = {
    allowSearchableDisplay: number
    fileName: string
    id: number
    isAttach: number
    isSearchable: number
    link: string
    size: number
    lastProcessedOn?: string
    updatedDate: number
    updatedDateTime?: string
}

export type TProfileSettingMeta = {
    isAttach: boolean
    isBasicInfo: boolean
    isContactInformation: boolean
    isWorkingPreference: boolean
    isWorkingPreferenceWeb: boolean
}

export type TProfileSettingInfo = {
    resumeSetting: {
        attachInfo: TResumeSettingItem[]
        profileInfo: TResumeSettingItem
    }
    searchableStatus: number
    unContactable: number
    meta: TProfileSettingMeta
    resumeSettingOption: {
        chooseCV: string
        chooseRefreshType: number
        resumeId?: number
        summary?: string
    }
}

export enum ERefreshProfile {
    Unset = 0,
    Daily = 1,
    Weekly = 2
}
export type TProfileSetting = {
    chooseCV: 'attach-cv' | 'profile'
    resumeId: number
    chooseRefreshType: ERefreshProfile
    summary: string
}

export type AppStoreState = {
    langCode: ELanguage
    device: EDevice
    user: TAuthUser | null
    t: (key: string) => string
    reverseTranslate: (key: string) => string
    isMobile: boolean
    lastBlockUpdate: EProfileStrengthForm[]
    isDowngraded: boolean
    resume: {
        contactInfo: {
            phone: number | null
        }
        highestDegreeId: number
    }
    workHistoryList: any
    listMoreSectionsLength: {
        activity: number
        certification: number
    }
    breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    profileLevel: TProfileLevelItem
    profileSettingInfo: TProfileSettingInfo
    loading: boolean
    profileInfo: TProfileInfo
    recommedation: TRecommendation | null
    recommendations: TRecommendation[]
    resumeDetail: IProfileData | null
    isShowPopupSetting: boolean
}

export const initAppStoreState: AppStoreState = {
    langCode: ELanguage.VI,
    device: EDevice.TABLET,
    user: null,
    t: createTFunction(ELanguage.VI),
    reverseTranslate: createReverseTranslateFunction(ELanguage.VI),
    isMobile: false,
    lastBlockUpdate: [],
    isDowngraded: false,
    resume: {
        contactInfo: {
            phone: null
        },
        highestDegreeId: 0
    },
    workHistoryList: [],
    listMoreSectionsLength: {
        activity: 0,
        certification: 0
    },
    breakpoint: 'md',
    profileLevel: {
        level: 0,
        passedPoints: 0,
        totalPoints: 0,
        status: '',
        profileStrength: 0
    },
    profileSettingInfo: {
        resumeSetting: {
            attachInfo: [
                {
                    allowSearchableDisplay: 0,
                    fileName: '',
                    link: '',
                    size: 0,
                    id: 0,
                    isAttach: 1,
                    isSearchable: 0,
                    updatedDate: 0
                }
            ],
            profileInfo: {
                allowSearchableDisplay: 0,
                fileName: '',
                link: '',
                size: 0,
                id: 0,
                isAttach: 0,
                isSearchable: 0,
                updatedDate: 0
            }
        },
        searchableStatus: 0,
        unContactable: 0,
        meta: {
            isAttach: false,
            isBasicInfo: false,
            isContactInformation: false,
            isWorkingPreference: false,
            isWorkingPreferenceWeb: false
        },
        resumeSettingOption: {
            chooseCV: '',
            chooseRefreshType: 0,
            resumeId: 0
        }
    },
    loading: false,
    profileInfo: {
        userId: 0,
        resumeId: 0,
        firstName: '',
        lastName: '',
        fullName: '',
        highestDegreeId: 0,
        isDownload: false,
        isOpenToWork: 0,
        jobTitle: '',
        jobLevelId: 0,
        yearsExperience: -1,
        avatar: '',
        lastCompany: '',
        wowcvTemplateId: 0,
        currentSalary: 0,
        currentIndustries: [],
        currentJobFunction: []
    },
    recommedation: null,
    recommendations: [],
    resumeDetail: null,
    isShowPopupSetting: false
}

export type TDispatchAction = {
    type: string
    payload?: any
}

const isOptOutUserAgentFilter = process.env.NEXT_PUBLIC_POSTHOG_OPT_OUT_USERAGENT_FILTER === '1'

const StoreContext = React.createContext<{
    state: AppStoreState
    dispatch: React.Dispatch<TDispatchAction>
}>({
    state: initAppStoreState,
    dispatch: () => null
})

const GlobalProvider = ({ children, langCode = ELanguage.VI }: TStoreProps & Partial<AppStoreState>) => {
    const [state, dispatch] = useReducer(reducer, {
        ...initAppStoreState,
        langCode
    })

    const t = createTFunction(state.langCode)
    const reverseTranslate = createReverseTranslateFunction(state.langCode)

    return (
        <StoreContext.Provider value={{ state: { ...state, t, reverseTranslate }, dispatch }}>
            {children}
        </StoreContext.Provider>
    )
}

const AppStoreProvider = ({ children, langCode = ELanguage.VI }: TStoreProps & Partial<AppStoreState>) => {
    return (
        <GlobalProvider langCode={langCode}>
            <HogProvider
                posthogConfig={{
                    key: process.env.NEXT_PUBLIC_POSTHOG_API_KEY ?? '',
                    host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? '',
                    options: {
                        optOutUserAgentFilter: isOptOutUserAgentFilter
                    }
                }}
            >
                <ProfileSettingContainer>
                    <JobAlertProvider>{children}</JobAlertProvider>
                </ProfileSettingContainer>
            </HogProvider>
        </GlobalProvider>
    )
}

const useStore = () => useContext(StoreContext)
const useJobAlertStore = () => useContext(JobAlertContext)

export { AppStoreProvider, useStore, useJobAlertStore }
